import { functions } from '../config/firebase';
import { httpsCallable } from 'firebase/functions';
import { logAudit, AUDIT_ACTIONS } from './auditService';

const IS_DEMO = process.env.REACT_APP_DEMO_MODE === 'true';

// Demo mode placeholder result
const DEMO_AI_RESULT = {
  imageBase64: null,
  mimeType: 'image/png',
  preset: 'fullMakeover',
  generationsRemaining: 47,
  isDemoPlaceholder: true,
};

// Available AI preset options
export const AI_PRESETS = [
  {
    id: 'whiten',
    name: 'Whiten & Brighten',
    description: 'Professional whitening to VITA shade B1',
  },
  {
    id: 'straighten',
    name: 'Straighten & Align',
    description: 'Digital orthodontic alignment',
  },
  {
    id: 'fullMakeover',
    name: 'Full Smile Makeover',
    description: 'Whiten, straighten, proportion, and perfect',
  },
  {
    id: 'closeGaps',
    name: 'Close Gaps',
    description: 'Close diastemas and spacing',
  },
];

/**
 * Extract the canvas background image as a base64 JPEG string.
 */
export const extractCanvasImageBase64 = (fabricCanvas) => {
  if (!fabricCanvas || !fabricCanvas.backgroundImage) return null;

  const bgImage = fabricCanvas.backgroundImage;
  const element = bgImage.getElement();
  if (!element) return null;

  const tempCanvas = document.createElement('canvas');
  tempCanvas.width = bgImage.width;
  tempCanvas.height = bgImage.height;
  const ctx = tempCanvas.getContext('2d');
  ctx.drawImage(element, 0, 0, bgImage.width, bgImage.height);

  const dataURL = tempCanvas.toDataURL('image/jpeg', 0.85);
  const base64 = dataURL.split(',')[1];

  return { base64, mimeType: 'image/jpeg' };
};

/**
 * Generate an AI smile design via Cloud Function.
 */
export const generateAiSmileDesign = async ({
  fabricCanvas,
  preset,
  customPrompt,
  practiceId,
  userId,
  patientId,
  caseId,
}) => {
  if (IS_DEMO) {
    // Simulate network delay in demo mode
    await new Promise(resolve => setTimeout(resolve, 2500));
    return { ...DEMO_AI_RESULT };
  }

  // Extract image from canvas background
  const imageData = extractCanvasImageBase64(fabricCanvas);
  if (!imageData) {
    throw new Error('No photo loaded on the canvas.');
  }

  const callable = httpsCallable(functions, 'generateSmileDesign', {
    timeout: 120000,
  });

  try {
    const result = await callable({
      imageBase64: imageData.base64,
      mimeType: imageData.mimeType,
      preset: preset || null,
      customPrompt: customPrompt || null,
      practiceId,
    });

    // Audit log
    logAudit({
      practiceId,
      userId,
      action: AUDIT_ACTIONS.CREATE,
      resourceType: 'aiSmileDesign',
      resourceId: `ai-${Date.now()}`,
      details: { preset: preset || 'custom', patientId, caseId },
    });

    return result.data;
  } catch (error) {
    console.error('AI generation failed:', error);

    if (error.code === 'functions/resource-exhausted') {
      throw new Error(error.message || 'Monthly generation limit reached.');
    }
    if (error.code === 'functions/unauthenticated') {
      throw new Error('Please log in to use AI features.');
    }
    throw new Error(error.message || 'AI generation failed. Please try again.');
  }
};
